/**
 * Password hashing and validation utilities
 * Uses argon2 for secure password hashing
 */

import * as argon2 from 'argon2'
import * as crypto from 'crypto'

export interface PasswordHashOptions {
  timeCost?: number
  memory?: number
  parallelism?: number
  type?: 'argon2d' | 'argon2i' | 'argon2id'
}

export interface PasswordValidationRules {
  minLength?: number
  maxLength?: number
  requireLowercase?: boolean
  requireUppercase?: boolean
  requireNumbers?: boolean
  requireSpecialChars?: boolean
  specialCharsPattern?: string
}

const DEFAULT_HASH_OPTIONS: PasswordHashOptions = {
  timeCost: 3,
  memory: 65536, // 64MB
  parallelism: 4,
  type: 'argon2id'
}

const DEFAULT_VALIDATION_RULES: PasswordValidationRules = {
  minLength: 8,
  maxLength: 128,
  requireLowercase: true,
  requireUppercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  specialCharsPattern: '!@#$%^&*()_+\\-=\\[\\]{};\':"\\\\|,.<>\\/?'
}

/**
 * Hash a password using Argon2
 */
export async function hashPassword(
  password: string,
  options: PasswordHashOptions = {}
): Promise<string> {
  const opts = { ...DEFAULT_HASH_OPTIONS, ...options }
  const typeMap: Record<string, any> = {
    'argon2d': 0,
    'argon2i': 1,
    'argon2id': 2
  }

  return argon2.hash(password, {
    timeCost: opts.timeCost,
    memoryCost: opts.memory,
    parallelism: opts.parallelism,
    type: typeMap[opts.type || 'argon2id'],
    raw: false
  })
}

/**
 * Verify a password against its hash
 */
export async function verifyPassword(password: string, hash: string): Promise<boolean> {
  try {
    return await argon2.verify(hash, password)
  } catch (error) {
    return false
  }
}

/**
 * Validate password strength
 */
export function validatePasswordStrength(
  password: string,
  rules: PasswordValidationRules = {}
): {
  valid: boolean
  errors: string[]
} {
  const config = { ...DEFAULT_VALIDATION_RULES, ...rules }
  const errors: string[] = []

  if (config.minLength && password.length < config.minLength) {
    errors.push(`Password must be at least ${config.minLength} characters long`)
  }

  if (config.maxLength && password.length > config.maxLength) {
    errors.push(`Password must be less than ${config.maxLength} characters`)
  }

  if (config.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }

  if (config.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }

  if (config.requireNumbers && !/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number')
  }

  if (config.requireSpecialChars && config.specialCharsPattern) {
    const pattern = new RegExp(`[${config.specialCharsPattern}]`)
    if (!pattern.test(password)) {
      errors.push('Password must contain at least one special character')
    }
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Generate a secure password reset token
 */
export function generatePasswordResetToken(): { token: string; hash: string } {
  const token = crypto.randomBytes(32).toString('hex')
  const hash = crypto.createHash('sha256').update(token).digest('hex')
  return { token, hash }
}

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email) && email.length <= 254
}

/**
 * Normalize email (lowercase)
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim()
}
